/**
 * RebootDeviceDialog (DE-277) — operator-initiated remote reboot of a device's
 * Linux box, delivered as an IoT Job the on-device agent executes behind its
 * own safety gates. Super-admin only; the button lives in the Status card
 * header (SimCard's link idiom) via RebootDeviceControl below.
 *
 * The dialog is deliberately explicit about consequences: the outage, demo
 * interruption under force, staged updates applying on the way up, and the
 * queue-if-offline + 24 h expiry semantics. The device itself still refuses
 * unsafe reboots (uncommitted A/B trial, OS install in flight) — this UI is
 * the consent step, not the safety mechanism.
 *
 * Double-click guard: while an active reboot job exists the button disables —
 * two queued jobs would mean two sequential reboots (AWS delivers the second
 * after the first boot resolves it).
 */
import { useState } from 'react';
import { useDeviceUpdateJobs, useMe, useRebootDevice } from '../api/hooks';
import { useToast } from './Toast';

export function RebootDeviceControl({ thing }: { thing: string }) {
  const me = useMe();
  const jobs = useDeviceUpdateJobs(thing);
  const [open, setOpen] = useState(false);

  if (me.data?.role !== 'super-admin') return null;

  const active = jobs.data?.active ?? [];
  const rebootPending = active.some((j) => j.kind === 'reboot');
  const updateInFlight = active.some((j) => j.kind === 'mender' || j.kind === 'release');

  return (
    <>
      <button
        type="button"
        disabled={rebootPending}
        onClick={() => setOpen(true)}
        title={rebootPending ? 'A reboot job is already pending for this device' : undefined}
        className="text-warn hover:underline text-xs disabled:opacity-50 disabled:no-underline"
      >
        {rebootPending ? 'Reboot pending…' : 'Reboot device…'}
      </button>
      {open && (
        <RebootDeviceDialog
          thing={thing}
          updateInFlight={updateInFlight}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function RebootDeviceDialog({
  thing,
  updateInFlight,
  onClose,
}: {
  thing: string;
  updateInFlight: boolean;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const [force, setForce] = useState(false);
  const toast = useToast();
  const reboot = useRebootDevice();
  const busy = reboot.isPending;

  const submit = async () => {
    if (busy) return;
    try {
      const r = await reboot.mutateAsync({ thing, force, reason: reason.trim() });
      toast.success(`Reboot job ${r.jobId} dispatched`);
      onClose();
    } catch (err) {
      toast.error((err as Error).message || 'reboot dispatch failed');
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-modal-top"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="reboot-device-title"
    >
      <div className="card w-full max-w-md space-y-4 shadow-xl">
        <div>
          <h2 id="reboot-device-title" className="font-semibold">
            Reboot device
          </h2>
          <p className="text-xs text-muted mt-1">
            Reboots the Linux box of <span className="font-mono">{thing}</span> — roughly
            60–90 seconds of outage. Any staged updates (content, app) apply on the way
            back up. If the device is offline the job queues and expires after 24 hours.
            The device refuses the reboot itself during an uncommitted OS update.
          </p>
        </div>

        {updateInFlight && (
          <p className="text-xs text-warn">
            An update is currently in flight on this device — the reboot will queue
            behind it and run only when the update finishes.
          </p>
        )}

        <label className="block space-y-1">
          <span className="text-xs text-muted">Reason (optional, recorded in the audit trail)</span>
          <input
            type="text"
            value={reason}
            maxLength={200}
            disabled={busy}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. apply staged content for release 2026.08"
            className="w-full rounded-md border border-border bg-transparent px-2 py-1 text-sm"
          />
        </label>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={force}
            disabled={busy}
            onChange={(e) => setForce(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            Interrupt an active demo (force)
            <span className="block text-xs text-muted">
              Without this, the device waits up to 10 minutes for the demo to go idle and
              gives up rather than rebooting mid-presentation.
            </span>
          </span>
        </label>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            autoFocus
            className="px-3 py-1.5 text-sm rounded-md border border-border hover:bg-soft"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="px-3 py-1.5 text-sm rounded-md bg-warn text-white hover:opacity-90 disabled:opacity-50"
          >
            {busy ? 'Dispatching…' : 'Reboot device'}
          </button>
        </div>
      </div>
    </div>
  );
}
